// API Configuration
// Use empty string to leverage Vite proxy in development
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  CURRENT_USER: '/api/v1/auth/me',

  // Tasks
  START_ANALYSIS: '/api/v1/tasks',
  GET_TASK: '/api/v1/tasks/:taskId',
  LIST_TASKS: '/api/v1/tasks',
  GET_TASK_REPORTS: '/api/v1/tasks/:taskId/reports',
  CANCEL_TASK: '/api/v1/tasks/:taskId/cancel',
  RETRY_TASK: '/api/v1/tasks/:taskId/retry',

  // Billing
  BILLING_SUMMARY: '/api/v1/billing/summary',
  TASK_QUOTE: '/api/v1/billing/quote',

  // Payments
  RETRY_PAYMENT: '/api/v1/payments/retry/:taskId',
  PURCHASE_HISTORY: '/api/v1/payments/history/query',

  // Profile
  PROFILE: '/api/v1/profile',
  UPDATE_PASSWORD: '/api/v1/profile/password',
  UPDATE_LLM_PROVIDER: '/api/v1/profile/llm-provider',
  LLM_OPTIONS: '/api/v1/profile/llm-options',
  DELETE_ACCOUNT: '/api/v1/profile',

  // WebSocket (handled via relative URLs)
  WS_ANALYSIS: '/ws/analysis/:taskId',
}

// Task Status
export const TASK_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
}

// Agent Status
export const AGENT_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
}

// Workflow Stages
export const WORKFLOW_STAGES = {
  ANALYSIS: 'analysis',
  RESEARCH: 'research',
  TRADING: 'trading',
  RISK: 'risk',
}

// Agent Teams
export const AGENT_TEAMS = {
  ANALYSTS: [
    { id: 'market', name: 'Market Analyst', stage: WORKFLOW_STAGES.ANALYSIS },
    { id: 'social', name: 'Social Analyst', stage: WORKFLOW_STAGES.ANALYSIS },
    { id: 'news', name: 'News Analyst', stage: WORKFLOW_STAGES.ANALYSIS },
    { id: 'fundamentals', name: 'Fundamentals Analyst', stage: WORKFLOW_STAGES.ANALYSIS },
  ],
  RESEARCHERS: [
    { id: 'bull', name: 'Bull Researcher', stage: WORKFLOW_STAGES.RESEARCH },
    { id: 'bear', name: 'Bear Researcher', stage: WORKFLOW_STAGES.RESEARCH },
    { id: 'research-manager', name: 'Research Manager', stage: WORKFLOW_STAGES.RESEARCH },
  ],
  TRADERS: [
    { id: 'trader', name: 'Trader', stage: WORKFLOW_STAGES.TRADING },
  ],
  RISK_MANAGERS: [
    { id: 'risk-risky', name: 'Aggressive Analyst', stage: WORKFLOW_STAGES.RISK },
    { id: 'risk-safe', name: 'Conservative Analyst', stage: WORKFLOW_STAGES.RISK },
    { id: 'risk-neutral', name: 'Neutral Analyst', stage: WORKFLOW_STAGES.RISK },
    { id: 'risk-manager', name: 'Risk Manager', stage: WORKFLOW_STAGES.RISK },
  ],
}

// All Agents
export const ALL_AGENTS = [
  ...AGENT_TEAMS.ANALYSTS,
  ...AGENT_TEAMS.RESEARCHERS,
  ...AGENT_TEAMS.TRADERS,
  ...AGENT_TEAMS.RISK_MANAGERS,
]

// Research Depth Options
export const RESEARCH_DEPTH_OPTIONS = [
  { value: 1, label: 'Shallow', description: 'Quick analysis with basic insights' },
  { value: 2, label: 'Medium', description: 'Moderate depth with standard research' },
  { value: 3, label: 'Deep', description: 'In-depth analysis with comprehensive research' },
]

// Decision Types
export const DECISION_TYPES = {
  BUY: 'buy',
  SELL: 'sell',
  HOLD: 'hold',
}

export const PAYMENT_STATUS = {
  FREE: 'FREE',
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  PAID: 'PAID',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  PAYMENT_EXPIRED: 'PAYMENT_EXPIRED',
  REFUNDED: 'REFUNDED',
}

export const PAYMENT_STATUS_COLORS = {
  FREE: 'green',
  PAID: 'blue',
  AWAITING_PAYMENT: 'orange',
  PAYMENT_FAILED: 'red',
  PAYMENT_EXPIRED: 'volcano',
  REFUNDED: 'cyan',
}

export const PAYMENT_STATUS_LABELS = {
  FREE: 'Free',
  AWAITING_PAYMENT: 'Awaiting Payment',
  PAID: 'Paid',
  PAYMENT_FAILED: 'Payment Failed',
  PAYMENT_EXPIRED: 'Payment Expired',
  REFUNDED: 'Refunded',
}

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'AUTH_TOKEN',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  ID_TOKEN: 'ID_TOKEN',
  USER_INFO: 'USER_INFO',
  THEME: 'tradingagents_theme',
}

// Message Display Limits
export const MAX_MESSAGES_DISPLAY = 100

// Auto-reconnect settings
export const WS_RECONNECT_INTERVAL = 3000
export const WS_MAX_RECONNECT_ATTEMPTS = 5
export const WS_HEARTBEAT_INTERVAL = 30000

// Date Formats
export const DATE_FORMAT = 'YYYY-MM-DD'
export const TIME_FORMAT = 'HH:mm:ss'
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

// Helper Functions

/**
 * Parse API URL template with parameters
 * Example: parseApiUrl('/api/v1/tasks/:taskId', { taskId: '123' }) => '/api/v1/tasks/123'
 */
export const parseApiUrl = (urlTemplate, params = {}) => {
  let url = urlTemplate
  Object.keys(params).forEach((key) => {
    url = url.replace(`:${key}`, params[key])
  })
  return url
}
